import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { VerificationService } from 'app/core/verification/verification.service';
import { mergeMap, map, catchError, of } from 'rxjs';
import {
    GetUserVerification,
    VerificationActions,
    SuccessVerification,
    FailVerification,
    UpdatePaymentSystemVerification,
    UpdateIdentityVerification,
    SuccessIdentityVerification,
} from '../actions/verification.actions';
import { IdentityVerificationStatus } from '../models/verifications.model';
import { UserVerificationState } from '../state/verification.state';
import { ShowAlert } from '../actions/alert.actions';

@Injectable()
export class VerificationEffects {
    constructor(
        private actions$: Actions,
        private verificationService: VerificationService
    ) {}

    getUserVerification$ = createEffect(() =>
        this.actions$.pipe(
            ofType<GetUserVerification>(
                VerificationActions.getUserVerification
            ),
            mergeMap((action) =>
                this.verificationService
                    .getUserVerificationData(
                        action.payload.userId,
                        action.payload.merchant.toLocaleLowerCase()
                    )
                    .pipe(
                        map((data: UserVerificationState) => {
                            if (this.verificationService.isUserNotFound(data)) {
                                return new FailVerification('User not found!');
                            }
                            const payload: UserVerificationState = {
                                identityVerification: data.identityVerification,
                                psVerification: {
                                    verified: data.psVerification.verified,
                                    notVerified:
                                        data.psVerification.notVerified,
                                },
                                paymentSystemSafetiness:
                                    data.paymentSystemSafetiness,
                                userId: action.payload.userId,
                                merchant:
                                    action.payload.merchant.toLocaleLowerCase(),
                            };
                            return new SuccessVerification(payload);
                        }),
                        catchError((error) =>
                            of(
                                new FailVerification(
                                    'Failed to fetch user verification data.'
                                )
                            )
                        )
                    )
            )
        )
    );

    updatePaymentSystemVerification$ = createEffect(() =>
        this.actions$.pipe(
            ofType<UpdatePaymentSystemVerification>(
                VerificationActions.updatePaymentSystemVerification
            ),
            mergeMap((action) =>
                this.verificationService
                    .updatePaymentSystemVerification(action.payload)
                    .pipe(
                        map(() => new GetUserVerification(action.payload)),
                        catchError((error) =>
                            of(
                                new FailVerification(
                                    'Failed to update verification!' + error
                                )
                            )
                        )
                    )
            )
        )
    );

    updateIdentityVerification$ = createEffect(() =>
        this.actions$.pipe(
            ofType<UpdateIdentityVerification>(
                VerificationActions.updateIdentityVerification
            ),
            mergeMap((action) =>
                this.verificationService
                    .updateUserIdentityVerification(action.payload)
                    .pipe(
                        map(
                            () =>
                                new SuccessIdentityVerification({
                                    identityVerification: action.payload
                                        .isVerified
                                        ? IdentityVerificationStatus.VERIFIED
                                        : IdentityVerificationStatus.NOT_VERIFIED,
                                })
                        ),
                        catchError((error) =>
                            of(
                                new FailVerification(
                                    'Failed to update verification!' + error
                                )
                            )
                        )
                    )
            )
        )
    );

    failVerificationEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType<FailVerification>(VerificationActions.failVerification),
            map(
                (action) =>
                    new ShowAlert({
                        message:
                            action.payload ||
                            'Operation failed! Please try again.',
                        type: 'error',
                    })
            )
        )
    );

    successVerificationEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType<SuccessVerification>(
                VerificationActions.successVerification,
                VerificationActions.successIdentityVerification
            ),
            map(
                () =>
                    new ShowAlert({
                        message: 'Operation successful!',
                        type: 'success',
                    })
            )
        )
    );
}
